import { supabase } from "@/integrations/supabase/client";

/**
 * Shape of `context_snapshot.ai_context` written by the planner / drafter /
 * publication-preparer edge functions (see `_shared/aiContext.ts`). All fields
 * are optional because older runs may not have a snapshot at all.
 */
export type AIContextSnapshot = {
  version?: number;
  voice_profile_id?: string | null;
  avatar_id?: string | null;
  writing_sample_ids?: string[];
  
  framework_present?: boolean;
  source_of_truth_ids?: string[];
  overrides_applied?: string[];
  assembled_at?: string;
  model?: string | null;
};

export type ResolvedContextSnapshot = {
  voiceLabel: string | null;
  avatarLabel: string | null;
  sampleLabels: string[];
  sourceOfTruthLabels: string[];
  
  frameworkPresent: boolean;
  overridesApplied: string[];
  assembledAt: string | null;
  model: string | null;
  isEmpty: boolean;
};

/** Pull `ai_context` out of a row's `context_snapshot` jsonb safely. */
export const extractAIContext = (
  contextSnapshot: unknown,
): AIContextSnapshot | null => {
  if (!contextSnapshot || typeof contextSnapshot !== "object") return null;
  const ai = (contextSnapshot as Record<string, unknown>).ai_context;
  if (!ai || typeof ai !== "object") return null;
  return ai as AIContextSnapshot;
};

const stringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

/**
 * Resolve UUIDs in a snapshot into human-readable labels. Runs a few small
 * `select id, name in (...)` queries scoped to the workspace.
 */
export const resolveContextSnapshot = async (
  workspaceId: string,
  snapshot: AIContextSnapshot | null,
): Promise<ResolvedContextSnapshot> => {
  if (!snapshot) {
    return {
      voiceLabel: null,
      avatarLabel: null,
      sampleLabels: [],
      sourceOfTruthLabels: [],
      
      frameworkPresent: false,
      overridesApplied: [],
      assembledAt: null,
      model: null,
      isEmpty: true,
    };
  }

  const sampleIds = stringArray(snapshot.writing_sample_ids);
  const sotIds = stringArray(snapshot.source_of_truth_ids);

  const [voiceRes, avatarRes, sotsRes] = await Promise.all([
    snapshot.voice_profile_id
      ? supabase
          .from("content_voice_profiles")
          .select("id, title")
          .eq("workspace_id", workspaceId)
          .eq("id", snapshot.voice_profile_id)
          .maybeSingle()
      : Promise.resolve({ data: null }),
    snapshot.avatar_id
      ? supabase
          .from("content_avatars")
          .select("id, name")
          .eq("workspace_id", workspaceId)
          .eq("id", snapshot.avatar_id)
          .maybeSingle()
      : Promise.resolve({ data: null }),
    sotIds.length
      ? supabase
          .from("content_sources")
          .select("id, title")
          .eq("workspace_id", workspaceId)
          .in("id", sotIds)
      : Promise.resolve({ data: [] }),
  ]);
  // `sampleIds` from the snapshot are no longer resolved — pasted writing
  // samples were retired in favor of Corpus-tagged documents.
  void sampleIds;

  return {
    voiceLabel: (voiceRes?.data as { title?: string } | null)?.title ?? null,
    avatarLabel: (avatarRes?.data as { name?: string } | null)?.name ?? null,
    sampleLabels: [],
    sourceOfTruthLabels: ((sotsRes?.data ?? []) as Array<{ title: string }>).map((s) => s.title),
    
    frameworkPresent: snapshot.framework_present === true,
    overridesApplied: stringArray(snapshot.overrides_applied),
    assembledAt: snapshot.assembled_at ?? null,
    model: snapshot.model ?? null,
    isEmpty: false,
  };
};

/** Friendly label for an override key like "avatar:<uuid>" or "framework". */
export const formatOverrideLabel = (key: string): string => {
  const [kind] = key.split(":");
  switch (kind) {
    case "voice":
      return "Voice profile off";
    case "avatar":
      return "Avatar off";
    case "framework":
      return "Framework off";
    case "samples":
      return "Writing samples off";
    case "source_of_truth":
      return "Source-of-truth off";
    default:
      return `${kind} off`;
  }
};
